/**
 * «Estás parado en la empresa equivocada»: el motivo, y el botón para salir.
 *
 * El defecto, contado como lo vivió el vendedor
 * ---------------------------------------------
 * Arriba a la derecha hay un selector de empresa. En el mismo sistema conviven
 * la empresa comercial —la que vende trámites— y los consorcios, que tienen su
 * contabilidad pero **no venden**: liquidan expensas. Con un consorcio activo la
 * venta se anotaba igual, nadie avisaba nada, y días después el sistema la
 * rechazaba con un mensaje sobre el catálogo de impuestos. El error aparecía
 * lejos de su causa y hablando de algo que la persona nunca tocó.
 *
 * Qué hace este archivo, y qué NO hace
 * ------------------------------------
 * **No inventa ningún mecanismo de aviso.** Usa el que ya existe —
 * `views/MotivoBloqueo`— y se limita a conseguirle sus dos piezas:
 *
 * * **el motivo lo escribe el SERVIDOR.** Es el `disabled_reason` de la acción
 *   `venta_rapida` de `base.partner`, redactado por
 *   `sales/services/empresa_vendedora.py`. Acá no se compone ni una frase de
 *   negocio: si el backend deja de mandarlo, no se muestra nada.
 * * **la salida la arma el cliente**, porque es un control de la aplicación y no
 *   un texto: cambiar la empresa activa del selector. A qué empresa, lo dice la
 *   sesión (`can_sell` por empresa). Si hay **una sola** que puede vender se la
 *   propone por su nombre y a un clic, que es el caso de esta instalación; si
 *   hay varias no se elige por la persona y se la manda al selector; si no hay
 *   ninguna no se ofrece salida, porque no la hay.
 *
 * **`encadena` es siempre falso, y no es negociable**: el botón cambia de
 * empresa y **nada más**. No anota la venta, no la factura y no la reintenta. La
 * venta la carga la persona, después, viendo dónde está parada.
 */

import { useMetaQuery } from '@/components/shell/queries';
import type { ModelAction } from '@/lib/types';
import { useSessionStore } from '@/store/session';
import type { SalidaDeBloqueo } from '@/views/MotivoBloqueo';

/** El modelo del que cuelga la acción de venta rápida. */
const MODELO_CONTACTO = 'base.partner';

/**
 * La salida: cambiar la empresa activa a una que sí pueda vender.
 *
 * `null` cuando no hay salida que ofrecer —ninguna empresa habilitada puede
 * vender—, que es distinto de «hay una y todavía no la apretaron». Un cartel sin
 * salida sigue siendo mejor que un cartel que promete una que no existe.
 */
export function useSalidaAEmpresaQueVende(): SalidaDeBloqueo | null {
  const companies = useSessionStore((estado) => estado.companies);
  const activa = useSessionStore((estado) => estado.activeCompanyId);
  const setActiveCompany = useSessionStore((estado) => estado.setActiveCompany);

  const vendedoras = companies.filter(
    (company) => company.can_sell === true && company.id !== activa,
  );
  if (vendedoras.length === 0) return null;

  // Con dos o más no se elige por la persona. El motivo del servidor ya las
  // nombra a todas —«…cargá la venta desde la que factura: «A», «B».»— y el
  // selector está arriba, a la vista: inventar un botón que elija por ella sería
  // adivinar desde cuál de las dos razones sociales quiere facturar.
  if (vendedoras.length !== 1) return null;

  const destino = vendedoras[0];
  if (destino === undefined) return null;
  return {
    etiqueta: `Cambiar a ${destino.name}`,
    aclaracion:
      'Cambia la empresa activa del selector de arriba. No anota ni emite nada: después la venta la cargás vos.',
    ejecutar: () => {
      void setActiveCompany(destino.id);
    },
  };
}

export interface EstadoDeVentaRapida {
  /** Todavía no contestó el servidor. No se dibuja ni el botón ni el cartel. */
  cargando: boolean;
  /** El usuario tiene la acción (grupos y permisos). Sin esto no hay nada que decir. */
  existe: boolean;
  /** El backend la dejó usable con la empresa activa de ahora. */
  habilitada: boolean;
  /** El `disabled_reason` del servidor. Vacío cuando está habilitada. */
  motivo: string;
}

/**
 * ¿Se puede vender desde la empresa activa, y si no, por qué?
 *
 * Se pregunta por la metadata de `base.partner`: el backend ya poda por grupos
 * las acciones que no le tocan al usuario, y ahora además apaga `venta_rapida`
 * —con su motivo— cuando la empresa activa no puede facturar ventas (ver
 * `ServerAction.context_guard`). Dos preguntas distintas, una sola consulta, y
 * ninguna regla de negocio reimplementada de este lado.
 *
 * La clave incluye la empresa activa a propósito: el motivo depende de ella, así
 * que cambiar de empresa tiene que traer la respuesta nueva y no la cacheada.
 */
export function useEstadoDeVentaRapida(): EstadoDeVentaRapida {
  const autenticado = useSessionStore((estado) => estado.status === 'authenticated');
  // LA MISMA CONSULTA QUE TODOS, no una propia con otra clave.
  //
  // Esto pedía `api.fetchMeta(MODELO_CONTACTO)` a mano bajo la clave
  // «venta-rapida-habilitada». Es EXACTAMENTE la misma URL que ya pide
  // `useMetaQuery`, pero con otra clave de caché, así que React Query las trata
  // como dos consultas distintas y baja el archivo dos veces. Medido: la
  // metadata de `base.partner` —67 KB y 11 consultas SQL— se descargaba cinco
  // veces por sesión, y sólo cambia cuando se despliega.
  //
  // Usando el hook compartido, la segunda y las siguientes salen del caché.
  const consulta = useMetaQuery(autenticado ? MODELO_CONTACTO : null);

  if (consulta.isPending) {
    return { cargando: true, existe: false, habilitada: false, motivo: '' };
  }
  const accion =
    (consulta.data?.actions ?? []).find(
      (accion: ModelAction) => accion.name === 'venta_rapida',
    ) ?? null;
  if (consulta.isError || accion === null) {
    return { cargando: false, existe: false, habilitada: false, motivo: '' };
  }
  return {
    cargando: false,
    existe: true,
    habilitada: accion.enabled !== false,
    motivo: typeof accion.disabled_reason === 'string' ? accion.disabled_reason : '',
  };
}
